import type { ColumnDef } from "@tanstack/react-table";
import type { AdminSubscriptionItem } from "@/types/subscription";
import { SubscriptionCycle } from "@/types/enums";
import { PlanBadge } from "./plan-badge";
import { StatusBadge } from "./status-badge";
import { PlatformBadge } from "./platform-badge";
import { SubscriptionActions } from "./subscription-actions";
import { formatPriceWithCycle, formatNextPayment } from "@/lib/format";
import { ArrowUpDown } from "lucide-react";

export type SubscriptionAction = "refund" | "upgrade" | "extend" | "grantCredits";

export function getSubscriptionColumns(
  onAction: (subscription: AdminSubscriptionItem, action: SubscriptionAction) => void
): ColumnDef<AdminSubscriptionItem>[] {
  return [
    {
      accessorKey: "userEmail",
      header: ({ column }) => {
        return (
          <button
            type="button"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Sort by email"
          >
            이메일
            <ArrowUpDown className="h-3 w-3" />
          </button>
        );
      },
      cell: ({ row }) => {
        const email = row.original.userEmail;
        return (
          <div className="font-medium text-sm" title={email}>
            {email}
          </div>
        );
      },
    },
    {
      accessorKey: "userName",
      header: "이름",
      cell: ({ row }) => {
        return <div className="font-medium">{row.original.userName}</div>;
      },
    },
    {
      accessorKey: "plan",
      header: "플랜",
      cell: ({ row }) => {
        return <PlanBadge plan={row.original.plan} />;
      },
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      accessorKey: "status",
      header: "상태",
      cell: ({ row }) => {
        return <StatusBadge status={row.original.status} />;
      },
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      accessorKey: "price",
      header: ({ column }) => {
        return (
          <button
            type="button"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Sort by price"
          >
            가격
            <ArrowUpDown className="h-3 w-3" />
          </button>
        );
      },
      cell: ({ row }) => {
        const { price, subscriptionCycle } = row.original;
        return (
          <div className="font-mono text-sm">
            {formatPriceWithCycle(price, subscriptionCycle)}
          </div>
        );
      },
    },
    {
      accessorKey: "subscriptionCycle",
      header: "주기",
      cell: ({ row }) => {
        const cycle = row.original.subscriptionCycle;
        return (
          <div className="text-sm">
            {cycle === SubscriptionCycle.MONTHLY ? "월간" : "연간"}
          </div>
        );
      },
    },
    {
      accessorKey: "platform",
      header: "플랫폼",
      cell: ({ row }) => {
        const platform = row.original.platform;
        if (!platform) return <span className="text-muted-foreground">-</span>;
        return <PlatformBadge platform={platform} />;
      },
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      accessorKey: "nextPaymentDate",
      header: ({ column }) => {
        return (
          <button
            type="button"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Sort by next payment date"
          >
            다음 결제일
            <ArrowUpDown className="h-3 w-3" />
          </button>
        );
      },
      cell: ({ row }) => {
        const { text, isOverdue } = formatNextPayment(row.original.nextPaymentDate);
        return (
          <div className={`text-sm whitespace-nowrap ${isOverdue ? "text-destructive" : ""}`}>
            {text}
          </div>
        );
      },
    },
    {
      accessorKey: "userCredits.monthly",
      header: "월간 크레딧",
      cell: ({ row }) => {
        const credits = row.original.userCredits.monthly;
        return (
          <div className="font-mono text-sm" aria-label={`${credits} credits`}>
            {credits.toLocaleString()}
          </div>
        );
      },
    },
    {
      accessorKey: "userCredits.purchased",
      header: "구매 크레딧",
      cell: ({ row }) => {
        const credits = row.original.userCredits.purchased;
        return (
          <div className="font-mono text-sm" aria-label={`${credits} credits`}>
            {credits.toLocaleString()}
          </div>
        );
      },
    },
    {
      id: "actions",
      header: "작업",
      cell: ({ row }) => {
        return (
          <SubscriptionActions
            subscription={row.original}
            onAction={onAction}
          />
        );
      },
    },
  ];
}
